import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { SparePartsReportDataGrid } from '../../../components/datagrid/SparePartsReportDataGrid';
import { Loader } from '../../../components/loader/Loader';
import { useBreadcrumbs } from '../../../contexts/breadcrumbsContext';
import { useTranslation } from '../../../contexts/translation';
import SparePartReportFilter from '../../../forms/filter/sparePartReport/SparePartReportFilter';
import { useIsTaskCreatePermission } from '../../../hooks/useIsTaskCreatePermission';
import { useSaveDataGridFilter } from '../../../hooks/useSaveDataGridFilter';
import { useSessionStorage } from '../../../hooks/useSessionStorage';
import { PageContentWrapper } from '../../../layouts/contentWrapper/PageContentWrapper';
import { AssetSpareParts } from '../../../types/spareParts.types';
import { STORAGE_KEYS } from '../../../utils/localStorageKeys';
import { ReportType } from '../../../utils/reportTypeKeys';
import '../Reports.scss';

export function SparePartsReport() {
  const [data, setData] = useState<Array<AssetSpareParts> | null>([]);
  const [isFilterVisible, setFilterVisible] = useState<boolean>(true);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [loadSettingFromSession, setLoadSettingFromSession] = useState(false);
  const { translate } = useTranslation();
  const { pathname } = useLocation();
  const transFormName = 'SparePart.List';
  const [isDatagridFilterActive, setIsDatagridFilterActive] = useState<boolean>(false);

  const reportType = ReportType.SpareParts;

  const history = useHistory();
  // Načtení práv uživatele
  const [userHasPermissionToCreate, loadingPermission] = useIsTaskCreatePermission();
  // Reference na dataGrid. Nutné např. kvůli ukládání nastavení.
  const dataGridRef = useRef<any>(null);
  // Indikátor, zda se má provést reset filtru DataGridu i filtru mimo DataGrid
  const [resetFilterAction, setResetFilterAction] = useState<boolean>(false);

  const { getActualJsonState, loadFilter, resetFilter, saveFilter } = useSaveDataGridFilter(
    reportType.key,
    dataGridRef,
  );

  const [datagridSetting] = useSessionStorage(STORAGE_KEYS.SPARE_PARTS_REPORT_DATAGRID_DESKTOP, {});

  useEffect(() => {
    const navigatedAway = sessionStorage.getItem(
      `${STORAGE_KEYS.SPARE_PARTS_REPORT_DATAGRID_DESKTOP}_navigated_away`,
    );
    if (navigatedAway) {
      sessionStorage.removeItem(`${STORAGE_KEYS.SPARE_PARTS_REPORT_DATAGRID_DESKTOP}_navigated_away`);
      setLoadSettingFromSession(true);
      loadFilter(datagridSetting);
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.SPARE_PARTS_REPORT_DATAGRID_DESKTOP);
      setLoadSettingFromSession(false);
    }
  }, []);

  const { setValue } = useBreadcrumbs();

  useEffect(() => {
    setValue({ pageName: 'Spare Parts', steps: [{ name: 'Home', pathToCutCount: 1 }] });
    return () => {
      setValue(null);
    };
  }, []);

  useEffect(() => {
    if (resetFilterAction) {
      resetFilter();
      setResetFilterAction(false);
    }
    saveFilter();

    if (!dataGridRef.current || !dataGridRef.current.instance) return;
  }, [resetFilterAction]);

  if (!data) return <Loader />;

  return (
    <PageContentWrapper heading={isFilterVisible ? translate!('Spare Parts', transFormName) : ''}>
      <>
        <SparePartReportFilter
          dataGridRef={dataGridRef}
          onDataChange={setData}
          report={reportType}
          onFilterChange={setSelectedFilter}
          filterVisibility={isFilterVisible}
          loadDefaultSetting={!loadSettingFromSession}
        />
        {isDatagridFilterActive && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
            <span className="datagrid-active-filter">
              {translate!('You have active filter', 'Reports.Bar')}
            </span>
          </div>
        )}
        <SparePartsReportDataGrid
          dataGridRef={dataGridRef}
          data={data}
          resetFilter={() => setResetFilterAction(true)}
          userCanCreate={userHasPermissionToCreate}
          dataGridKey={reportType.key}
          heading={`${translate!('Spare Parts', transFormName)}${
            selectedFilter ? ` - ${selectedFilter}` : ''
          }`}
          filterVisibility={isFilterVisible}
          setFilterVisibility={setFilterVisible}
          filterChanged={(hasFilter) => {
            setIsDatagridFilterActive(hasFilter);
          }}
          noTopPadding={isDatagridFilterActive}
        />
      </>
    </PageContentWrapper>
  );
}
export default forwardRef(SparePartsReport);
